import React, { useState, useCallback, useMemo, useRef } from 'react';
import { emitVisitCompleteWhenPhotosSettle, emitVisitError } from '../lib/embedBridge';
import { usePreview, PreviewInterstitial } from './preview/PreviewShell';
import LabFlowIntent from './intake/LabFlowIntent';
import QuestionStep from './intake/QuestionStep';
import MultiSelectStep from './intake/MultiSelectStep';
import InputStep from './intake/InputStep';
import HeightWeightBmiStep from './intake/HeightWeightBmiStep';
import TextareaStep from './intake/TextareaStep';
import DisqualifiedStep from './intake/DisqualifiedStep';
import BasicContactStep from './intake/BasicContactStep';
import ConsentDropdownStep from './intake/ConsentDropdownStep';
import FileUploadStep from './intake/FileUploadStep';
import ShippingAddressStep from './intake/ShippingAddressStep';
import LabResultsEntryStep, { isCompleteResultRow } from './intake/LabResultsEntryStep';
import Footer from './intake/Footer';
import SquarePaymentForm from './intake/SquarePaymentForm';
import AccountSetupCta from './intake/AccountSetupCta';
import { RecommendationGate, BenefitList, TrustBanner } from './intake/checkoutUi';
import BrandLogo from '@/components/BrandLogo';
import { buildTenantUrl } from '@/lib/tenant';
import { useHandoffSeed, skipHandoffContact } from '@/lib/handoff';
import {
  buildDynamicLabAnalysisFlow,
  isDisqualifyingAnswer,
  LAB_ANALYSIS_PRICE,
  LAB_ANALYSIS_NAME,
  LAB_ANALYSIS_PLAN_SLUG,
} from '../data/labAnalysisData';
import { apiService } from '../services/api';
import {
  consentAnswerLabel,
  composeQaQuestionText,
  describeSubmitFailure,
  formatMultiSelectAnswer,
  splitHeightWeightAnswer,
} from '../lib/utils';
import { MutatingDots } from 'react-loader-spinner';

// Lab Analysis intake (Beluga visitType `labAnalysis`, Labs: Yes) — a general
// wellness lab-testing questionnaire with provider feedback. One-time lab-order
// charge (no medication / pharmacy), relayed via /beluga/create-visit-v2 with
// flow='labAnalysis'. See src/data/labAnalysisData.js + the BE PRD.

const LabAnalysisForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [slideKey, setSlideKey] = useState(0);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [visitResult, setVisitResult] = useState(null);
  const submittingRef = useRef(false);

  // §authenticated-patient handoff: prefill contact from the resolved ?pt= patient
  // (the basic-contact step is removed from the flow below).
  useHandoffSeed(setAnswers);

  // §pdf-extract (BE PR #230): the results-entry step handles the upload +
  // /ai/extract-lab-results itself; it hands the uploaded file up here so the full
  // document is relayed to the provider post-submit (see submitOrder).
  const setLabDoc = useCallback(
    (fileObj) => setAnswers((prev) => ({ ...prev, 'lab-results-upload': fileObj })),
    [],
  );

  const stagingTest =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('belugaEnv') === 'staging';

  // Flow is dynamic (conditional questions filtered by prior answers).
  const preview = usePreview();
  const flow = useMemo(() => preview.wrapFlow(skipHandoffContact(buildDynamicLabAnalysisFlow(answers))), [answers, preview]);
  const totalSteps = flow.length;
  const currentQuestion = flow[currentStep];

  const handleDisqualify = useCallback((reason) => {
    setIsDisqualified(true);
    setDisqualifyReason(reason);
    setSlideKey((p) => p + 1);
  }, []);

  const handleNext = useCallback(() => {
    setCurrentStep((p) => (p < totalSteps - 1 ? p + 1 : p));
    setSlideKey((p) => p + 1);
  }, [totalSteps]);

  const handleBack = useCallback(() => {
    setCurrentStep((p) => (p > 0 ? p - 1 : p));
    setSlideKey((p) => p + 1);
  }, []);

  const handleAnswer = useCallback(
    (questionId, answer) => {
      setAnswers((prev) => ({ ...prev, [questionId]: answer }));
      const dq = isDisqualifyingAnswer(questionId, answer);
      if (dq.disqualified) {
        handleDisqualify(dq.reason);
        return;
      }
      // Auto-advance single-selects only (multi-select needs a Continue click).
      if (currentQuestion?.type === 'single-select') {
        setTimeout(() => handleNext(), 200);
      }
    },
    [currentQuestion, handleNext, handleDisqualify],
  );

  const handleStartOver = useCallback(() => {
    setCurrentStep(0);
    setAnswers({});
    setIsComplete(false);
    setIsDisqualified(false);
    setDisqualifyReason('');
    setSlideKey((p) => p + 1);
  }, []);

  const submitOrder = useCallback(
    async (payment) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        setIsSubmitting(true);
        setAnswers((prev) => ({ ...prev, payment }));

        // Build the Q/A answers[] envelope the BE relayer expects. Excluded from
        // the Q/A section: address + lab results (dedicated body fields —
        // contact.address / results[]); basic-contact (already the named `contact`
        // object); identity-verification (relayed separately as a photo, and its
        // base64 must not bloat the Q/A). CRITICAL for /lab/receiveLabResults:
        // Beluga rejects the whole visit with "Missing values" if ANY field is
        // empty/null — so drop rows with a blank answer (e.g. the optional
        // doctor-questions when left empty).
        const QA_EXCLUDE = new Set([
          'shipping-address', 'lab-results', 'lab-results-upload',
          'payment', 'basic-contact', 'identity-verification',
        ]);
        const formattedAnswers = Object.entries(answers).flatMap(([questionId, answer]) => {
          if (QA_EXCLUDE.has(questionId)) return [];
          const split = splitHeightWeightAnswer(answer);
          if (split) return split;
          const slide = flow.find((q) => q.id === questionId);
          const consentLabel = consentAnswerLabel(slide, answer);
          let formattedAnswer;
          if (consentLabel !== null) {
            formattedAnswer = consentLabel;
          } else if (Array.isArray(answer)) {
            formattedAnswer = formatMultiSelectAnswer(answer);
          } else if (typeof answer === 'object' && answer !== null) {
            formattedAnswer = answer.preview ? `File: ${answer.fileName || 'uploaded'}` : JSON.stringify(answer);
          } else {
            formattedAnswer = String(answer);
          }
          // Skip empty-answer rows — Beluga's receiveLabResults treats an empty
          // value anywhere in the payload as "Missing values" (400).
          if (!String(formattedAnswer).trim()) return [];
          const questionText = composeQaQuestionText(slide) || questionId;
          const record = { question: questionText, answer: formattedAnswer, slide_id: questionId, rawAnswer: answer };
          if (slide && typeof slide.consentText === 'string' && slide.consentText.trim()) {
            record.consentBody = slide.consentText;
          }
          return [record];
        });

        const contact = answers['basic-contact'] || {};
        const shipping = answers['shipping-address'] || {};
        // Results (Beluga/Dhale 2026-08-06): the results-entry step yields rows
        // (typed, or AI-extracted-then-confirmed), gated to >=1 complete row — so
        // results[] is always non-empty (Beluga requires >=1). If the patient
        // uploaded a lab doc, it's ALSO relayed below.
        const results = (Array.isArray(answers['lab-results']) ? answers['lab-results'] : []).filter(
          isCompleteResultRow,
        );
        const paymentId =
          payment?.paymentId || payment?.payment_id || localStorage.getItem('fitpal_intake_payment_id') || '';

        const result = await apiService.submitForm(
          formattedAnswers,
          {
            first_name: contact.name?.split(' ')[0] || '',
            last_name: contact.name?.split(' ').slice(1).join(' ') || '',
            email: contact.email || '',
            phone: contact.phone || '',
            dob: contact.dob || '',
            // §results-submission: /lab/receiveLabResults requires the patient
            // address (BE PR #225 accepts it on contact or top-level).
            address: shipping.address1 || '',
            city: shipping.city || '',
            state: shipping.state || '',
            zip: shipping.zipCode || '',
          },
          paymentId,
          'labAnalysis', // flow
          'labAnalysis', // visitType
          // labAnalysis = REVIEW of the patient's existing results: send them as
          // results[]; BE sets testToTreat:false, pharmacyId:"N/A", strips empties.
          { results },
        );

        // Relay the required gov-ID photo post-submit (same pipeline as other flows).
        const idPhoto = answers['identity-verification'];
        if (idPhoto && idPhoto.preview && result?.masterId) {
          apiService
            .uploadVisitPhoto({
              masterId: result.masterId,
              visitId: result.visitId,
              slideId: 'identity-verification',
              photo: idPhoto,
            })
            .catch((err) => {
              console.warn('[uploadVisitPhoto] lab-analysis identity relay failed:', err?.response?.data || err.message);
            });
        }

        // §pdf-lab-upload (Beluga/Dhale 2026-08-06): if the patient uploaded a lab
        // report, relay the PDF/photo post-submit — same pipeline as TRT's
        // lab-image-upload — so the provider reviews the full document alongside
        // the confirmed results[].
        const labDoc = answers['lab-results-upload'];
        if (labDoc && labDoc.preview && result?.masterId) {
          apiService
            .uploadVisitPhoto({
              masterId: result.masterId,
              visitId: result.visitId,
              slideId: 'lab-results-upload',
              photo: labDoc,
            })
            .catch((err) => {
              console.warn('[uploadVisitPhoto] lab-analysis lab-results relay failed:', err?.response?.data || err.message);
            });
        }

        setVisitResult(result);

        // §embed-bridge: tell the dashboard what was created. Waits for any

        // in-flight photo relay first — the parent closes its modal on this

        // message, which unmounts the iframe and would abort the upload.

        emitVisitCompleteWhenPhotosSettle({

          masterId: result?.masterId,

          visitId: result?.visitId,

          scheduleLink: result?.scheduleLink,

        });
        setIsComplete(true);
        handleNext();
      } catch (error) {
        console.error('Lab analysis submission error:', error);
        // §embed-bridge: relay the RAW backend error — the dashboard reads
        // errorCode/refundId to tell the patient their card was refunded.
        emitVisitError(error);
        alert(describeSubmitFailure(error));
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [answers, flow, handleNext],
  );

  const renderStep = () => {
    if (isDisqualified) {
      return (
        <div key={slideKey} className="slide-wrapper slide-forward">
          <DisqualifiedStep reason={disqualifyReason} onStartOver={handleStartOver} />
        </div>
      );
    }
    if (!currentQuestion) return null;

    const commonProps = {
      question: preview.withIcons(currentQuestion),
      value: answers[currentQuestion.id],
      onAnswer: (a) => handleAnswer(currentQuestion.id, a),
      onNext: handleNext,
      onBack: handleBack,
      canGoBack: currentStep > 0,
      isLastStep: currentStep === totalSteps - 1,
      setLoading,
    };

    const content = (() => {
      switch (currentQuestion.type) {
        case 'single-select':
          return <QuestionStep {...commonProps} />;
        case 'multi-select':
          return <MultiSelectStep {...commonProps} />;
        case 'textarea':
          return <TextareaStep {...commonProps} />;
        case 'input':
        case 'number':
        case 'text':
          return <InputStep {...commonProps} />;
        case 'height-weight-bmi':
          return <HeightWeightBmiStep {...commonProps} onDisqualify={handleDisqualify} isOnTreatment={false} />;
        case 'basic-contact':
          return <BasicContactStep {...commonProps} />;
        case 'file-upload':
          return <FileUploadStep {...commonProps} />;
        case 'shipping-address':
          return <ShippingAddressStep {...commonProps} />;
        case 'lab-results':
          return <LabResultsEntryStep {...commonProps} onLabDocUpload={setLabDoc} />;
        case 'consent-dropdown':
          return <ConsentDropdownStep {...commonProps} onDisqualify={handleDisqualify} />;
        case 'checkout':
          return (
            <RecommendationGate
              product={{
                name: LAB_ANALYSIS_NAME,
                sub: 'Provider-reviewed wellness lab analysis with feedback',
                price: `$${LAB_ANALYSIS_PRICE}`,
              }}
              headline="Your lab results are ready for review."
              subhead="Confirm below — a licensed provider reviews the lab results you shared and sends you personalized feedback."
              aboutItems={[
                'Reviewed by a licensed U.S. provider',
                'Personalized feedback on your results',
                'Feedback delivered to your dashboard',
              ]}
            >
              <div data-testid="lab-analysis-checkout-step" className="max-w-md mx-auto">
                <div className="mb-5">
                  <p className="text-[#2d8a5f] font-semibold text-sm">You're in!</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Last step before your feedback.</h2>
                </div>

                <div className="rounded-3xl border border-[#2d8a5f]/30 bg-[#f0faf4] p-5 mb-3">
                  <div className="flex items-center gap-4 pb-4 border-b border-[#2d8a5f]/15">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-900 leading-snug">{LAB_ANALYSIS_NAME}</p>
                      <p className="text-sm text-gray-500">Provider review of your lab results</p>
                    </div>
                    <span className="text-2xl font-bold text-[#2d8a5f] flex-shrink-0">${LAB_ANALYSIS_PRICE}</span>
                  </div>
                  <BenefitList
                    className="pt-4"
                    items={['Send your lab results', 'Personalized results feedback', 'Feedback in your dashboard']}
                  />
                </div>

                <TrustBanner>
                  <span className="font-semibold">Wellness screening only</span> — not a diagnosis; review findings with your provider.
                </TrustBanner>

                <div className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Payment details</h3>
                  {stagingTest ? (
                    <div className="space-y-3">
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        ⚠️ Staging sign-off mode — Square is bypassed. Submits a test lab-analysis visit to Beluga <strong>staging</strong> with no charge.
                      </p>
                      <button
                        type="button"
                        data-testid="staging-bypass-pay"
                        disabled={isSubmitting}
                        onClick={() => submitOrder({ paymentId: 'STAGING_TEST_BYPASS' })}
                        className="w-full rounded-xl bg-[#2d8a5f] px-4 py-3 font-semibold text-white disabled:opacity-50"
                      >
                        {isSubmitting ? 'Submitting…' : '[Staging test] Skip payment & submit to Beluga staging'}
                      </button>
                    </div>
                  ) : (
                    <SquarePaymentForm
                      planId={LAB_ANALYSIS_PLAN_SLUG}
                      amount={LAB_ANALYSIS_PRICE * 100}
                      onComplete={submitOrder}
                      answers={answers}
                      setLoading={setLoading}
                      clearSavedProgress={() => {}}
                      primaryGoal="labAnalysis"
                      currentStep={currentStep}
                      timestamp={Date.now()}
                      email={answers['basic-contact']?.email}
                    />
                  )}
                </div>
              </div>
            </RecommendationGate>
          );
        case 'complete':
          return (
            <div className="max-w-xl mx-auto text-center py-10" data-testid="lab-analysis-complete-step">
              <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Thanks{answers['basic-contact']?.name ? `, ${answers['basic-contact'].name.split(' ')[0]}` : ''}!
              </h2>
              <p className="text-gray-500 mb-4">
                Your lab results have been submitted for review. A licensed provider will review them and
                send you personalized feedback by email and in your dashboard.
              </p>
              {visitResult?.masterId && (
                <div className="inline-flex flex-col items-center bg-gray-50 rounded-xl px-6 py-3">
                  <span className="text-xs uppercase tracking-wide text-gray-400">Order reference</span>
                  <span className="text-lg font-semibold text-gray-900" data-testid="lab-analysis-visit-ref">
                    {visitResult.masterId}
                  </span>
                </div>
              )}
              <AccountSetupCta email={answers['basic-contact']?.email} />
            </div>
          );
        case 'preview-interstitial':
          return <PreviewInterstitial step={currentQuestion} onNext={handleNext} />;
        default:
          return null;
      }
    })();

    return (
      <div key={slideKey} className="slide-wrapper slide-forward">
        {preview.frame(currentQuestion, content, handleBack)}
      </div>
    );
  };

  return (
    <>
      <div className="intake-form-loader" style={{ display: loading ? '' : 'none' }}>
        <MutatingDots />
      </div>
      <div
        className={`min-h-screen flex flex-col ${preview.rootClass}`}
        data-testid="lab-analysis-form"
        style={{ display: loading === false ? '' : 'none' }}
      >
        <header className="w-full py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {currentStep > 0 && !isComplete && !isDisqualified ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                data-testid="back-button"
                style={{ display: currentQuestion?.type === 'complete' ? 'none' : '' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Back</span>
              </button>
            ) : (
              <a href={buildTenantUrl('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0" data-testid="home-link">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Home</span>
              </a>
            )}
            <div className="text-center flex-1">
              <BrandLogo alt="FitPal" className="mx-auto" style={{ width: '400px', height: 'auto', maxWidth: '100%' }} />
              <p className="text-xs text-gray-500 mt-1">Wellness Lab Analysis</p>
            </div>
            <div className="w-16 flex-shrink-0" />
          </div>
        </header>

        {!isComplete && !isDisqualified && (
          <div className="w-full px-4 py-2">
            <div className="max-w-2xl mx-auto">
              {preview.progress(currentQuestion, currentStep, totalSteps)}
            </div>
          </div>
        )}

        {!isComplete && !isDisqualified && <LabFlowIntent flow="analysis" />}

        <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
          <div className="w-full max-w-2xl slide-container">{renderStep()}</div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default LabAnalysisForm;
